/**
 * Create Order Wizard
 *
 * 7-step wizard for creating new orders:
 * 1. Order Details - Transaction Type, Settlement Agency
 * 2. Property Information - Address, Unit, City, State, ZIP, County, Sale Price, Closing Date
 * 3. Buyer/Borrower - Primary buyer/borrower contact
 * 4. Seller - Primary seller contact
 * 5. Lender & Other Parties - Optional lender, agents
 * 6. Review & Create - Review all data, order number, notes
 * 7. First CD - Add first Closing Disclosure (optional)
 */

import { format, parse } from 'date-fns';
import {
  ArrowLeft,
  ArrowRight,
  Building,
  CalendarIcon,
  Check,
  FileText,
  Home,
  Plus,
  Upload,
  User,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useCreateOrder, useOrder, useOrderCDs, useUpdateOrder } from '../../hooks';
import type {
  CreateOrderInput,
  OrderPartyInput,
  OrderTransaction,
  TransactionType,
  UpdateOrderInput,
} from '../../types';
import { type ContactData, ContactSearch } from './ContactSearch';

// US states for address dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

interface WizardStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const STEPS: WizardStep[] = [
  {
    id: 1,
    title: 'Order Details',
    description: 'Transaction type and settlement',
    icon: <FileText className="h-4 w-4" />,
  },
  {
    id: 2,
    title: 'Property',
    description: 'Property address and details',
    icon: <Home className="h-4 w-4" />,
  },
  {
    id: 3,
    title: 'Buyer',
    description: 'Buyer/Borrower information',
    icon: <User className="h-4 w-4" />,
  },
  {
    id: 4,
    title: 'Seller',
    description: 'Seller information',
    icon: <User className="h-4 w-4" />,
  },
  {
    id: 5,
    title: 'Other Parties',
    description: 'Lender and agents (optional)',
    icon: <Users className="h-4 w-4" />,
  },
  {
    id: 6,
    title: 'Review',
    description: 'Review and create order',
    icon: <Check className="h-4 w-4" />,
  },
  {
    id: 7,
    title: 'First CD',
    description: 'Add first Closing Disclosure',
    icon: <Plus className="h-4 w-4" />,
  },
];

interface PartyFormData {
  name: string;
  email: string;
  phone: string;
  address_street: string;
  address_city: string;
  address_state: string;
  address_zip: string;
}

const emptyParty: PartyFormData = {
  name: '',
  email: '',
  phone: '',
  address_street: '',
  address_city: '',
  address_state: '',
  address_zip: '',
};

interface FormData {
  // Step 1: Order Details
  transaction_type: TransactionType;
  settlement_agency: string;

  // Step 2: Property Information
  property_address: string;
  property_unit: string;
  property_city: string;
  property_state: string;
  property_zip: string;
  property_county: string;
  sale_price: string;
  target_closing_date: string;

  // Step 3: Buyer/Borrower
  buyer: PartyFormData;

  // Step 4: Seller
  seller: PartyFormData;

  // Step 5: Other Parties
  lender: PartyFormData;
  listing_agent: PartyFormData;
  selling_agent: PartyFormData;

  // Step 6: Review & Create
  order_number: string;
  order_name: string;
  notes: string;
  use_auto_order_number: boolean;
}

const initialFormData: FormData = {
  transaction_type: 'purchase',
  settlement_agency: '',
  property_address: '',
  property_unit: '',
  property_city: '',
  property_state: '',
  property_zip: '',
  property_county: '',
  sale_price: '',
  target_closing_date: '',
  buyer: { ...emptyParty },
  seller: { ...emptyParty },
  lender: { ...emptyParty },
  listing_agent: { ...emptyParty },
  selling_agent: { ...emptyParty },
  order_number: '',
  order_name: '',
  notes: '',
  use_auto_order_number: true,
};

export default function CreateOrderWizard() {
  const navigate = useNavigate();
  const { id: orderId } = useParams<{ id: string }>();
  const isEditMode = !!orderId;
  const { setPageHeader, clearPageHeader } = usePageHeader();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [closingDateOpen, setClosingDateOpen] = useState(false);

  // Fetch existing order data when in edit mode
  const { data: existingOrder, isLoading: isLoadingOrder } = useOrder(orderId || '', {
    enabled: isEditMode,
  });

  const createOrder = useCreateOrder({
    onSuccess: (order) => {
      setCreatedOrderId(order.id);
      setCurrentStep(7);
    },
    onError: (error) => {
      setErrors({ submit: error.message });
    },
  });

  const updateOrder = useUpdateOrder({
    onSuccess: () => {
      // Show First CD step (same as create flow)
      setCreatedOrderId(orderId || null);
      setCurrentStep(7);
    },
    onError: (error) => {
      setErrors({ submit: error.message });
    },
  });

  // Fetch existing CDs for the order when on step 7
  const { data: existingCDs, isLoading: isLoadingCDs } = useOrderCDs(createdOrderId || '', {
    enabled: currentStep === 7 && !!createdOrderId,
  });

  // Filter out deleted CDs
  const activeCDs = existingCDs?.filter((cd: OrderTransaction) => cd.status !== 'deleted') || [];

  // Initialize form with existing order data in edit mode
  useEffect(() => {
    if (isEditMode && existingOrder && !isInitialized) {
      const buyerParty = existingOrder.parties?.find((p) => p.party_type === 'buyer');
      const sellerParty = existingOrder.parties?.find((p) => p.party_type === 'seller');
      const lenderParty = existingOrder.parties?.find((p) => p.party_type === 'lender');
      const listingAgentParty = existingOrder.parties?.find(
        (p) => p.party_type === 'listing_agent',
      );
      const sellingAgentParty = existingOrder.parties?.find(
        (p) => p.party_type === 'selling_agent',
      );

      setFormData({
        transaction_type: existingOrder.transaction_type || 'purchase',
        settlement_agency: existingOrder.settlement_agency || '',
        property_address: existingOrder.property_address || '',
        property_unit: existingOrder.property_unit || '',
        property_city: existingOrder.property_city || '',
        property_state: existingOrder.property_state || '',
        property_zip: existingOrder.property_zip || '',
        property_county: existingOrder.property_county || '',
        sale_price: existingOrder.sale_price?.toString() || '',
        target_closing_date: existingOrder.target_closing_date?.split('T')[0] || '',
        order_number: existingOrder.order_number || '',
        order_name: existingOrder.order_name || '',
        notes: existingOrder.notes || '',
        use_auto_order_number: false,
        buyer: {
          name: buyerParty?.name || '',
          email: buyerParty?.email || '',
          phone: buyerParty?.phone || '',
          address_street: buyerParty?.address_street || '',
          address_city: buyerParty?.address_city || '',
          address_state: buyerParty?.address_state || '',
          address_zip: buyerParty?.address_zip || '',
        },
        seller: {
          name: sellerParty?.name || '',
          email: sellerParty?.email || '',
          phone: sellerParty?.phone || '',
          address_street: sellerParty?.address_street || '',
          address_city: sellerParty?.address_city || '',
          address_state: sellerParty?.address_state || '',
          address_zip: sellerParty?.address_zip || '',
        },
        lender: {
          name: lenderParty?.name || '',
          email: lenderParty?.email || '',
          phone: lenderParty?.phone || '',
          address_street: lenderParty?.address_street || '',
          address_city: lenderParty?.address_city || '',
          address_state: lenderParty?.address_state || '',
          address_zip: lenderParty?.address_zip || '',
        },
        listing_agent: {
          name: listingAgentParty?.name || '',
          email: listingAgentParty?.email || '',
          phone: listingAgentParty?.phone || '',
          address_street: listingAgentParty?.address_street || '',
          address_city: listingAgentParty?.address_city || '',
          address_state: listingAgentParty?.address_state || '',
          address_zip: listingAgentParty?.address_zip || '',
        },
        selling_agent: {
          name: sellingAgentParty?.name || '',
          email: sellingAgentParty?.email || '',
          phone: sellingAgentParty?.phone || '',
          address_street: sellingAgentParty?.address_street || '',
          address_city: sellingAgentParty?.address_city || '',
          address_state: sellingAgentParty?.address_state || '',
          address_zip: sellingAgentParty?.address_zip || '',
        },
      });
      setIsInitialized(true);
    }
  }, [isEditMode, existingOrder, isInitialized]);

  // Generate placeholder order number (only in create mode)
  useEffect(() => {
    if (!isEditMode && formData.use_auto_order_number) {
      const year = new Date().getFullYear();
      setFormData((prev) => ({
        ...prev,
        order_number: `ORD-${year}-XXXX (auto)`,
      }));
    }
  }, [isEditMode, formData.use_auto_order_number]);

  // Set page header
  useEffect(() => {
    setPageHeader({
      title: isEditMode ? 'Edit Order' : 'Create New Order',
      breadcrumbs: [
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Orders', href: '/orders' },
        { label: isEditMode ? 'Edit Order' : 'New Order' },
      ],
    });
    return () => clearPageHeader();
  }, [setPageHeader, clearPageHeader, isEditMode]);

  const updateFormData = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field as string]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field as string];
        return newErrors;
      });
    }
  };

  const updatePartyData = (
    partyType: 'buyer' | 'seller' | 'lender' | 'listing_agent' | 'selling_agent',
    field: keyof PartyFormData,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [partyType]: {
        ...prev[partyType],
        [field]: value,
      },
    }));
  };

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};
    // Transaction type has a default, so no validation needed
    setErrors(newErrors);
    return true;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.property_address.trim()) {
      newErrors.property_address = 'Property address is required';
    }
    if (!formData.property_city.trim()) {
      newErrors.property_city = 'City is required';
    }
    if (!formData.property_state) {
      newErrors.property_state = 'State is required';
    }
    if (!formData.property_zip.trim()) {
      newErrors.property_zip = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.property_zip)) {
      newErrors.property_zip = 'Invalid ZIP code format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.buyer.name.trim()) {
      newErrors.buyer_name = 'Buyer name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep4 = (): boolean => {
    // Seller is optional for refinance
    const newErrors: Record<string, string> = {};

    if (formData.transaction_type === 'purchase' && !formData.seller.name.trim()) {
      newErrors.seller_name = 'Seller name is required for purchase transactions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep5 = (): boolean => {
    // All parties in step 5 are optional
    return true;
  };

  const validateStep6 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.use_auto_order_number && !formData.order_number.trim()) {
      newErrors.order_number = 'Order number is required when not auto-generating';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildParties = (): OrderPartyInput[] => {
    const parties: OrderPartyInput[] = [];

    // Buyer/Borrower
    if (formData.buyer.name.trim()) {
      parties.push({
        party_type: 'buyer',
        name: formData.buyer.name,
        email: formData.buyer.email || undefined,
        phone: formData.buyer.phone || undefined,
        address_street: formData.buyer.address_street || undefined,
        address_city: formData.buyer.address_city || undefined,
        address_state: formData.buyer.address_state || undefined,
        address_zip: formData.buyer.address_zip || undefined,
        is_primary: true,
      });
    }

    // Seller
    if (formData.seller.name.trim()) {
      parties.push({
        party_type: 'seller',
        name: formData.seller.name,
        email: formData.seller.email || undefined,
        phone: formData.seller.phone || undefined,
        address_street: formData.seller.address_street || undefined,
        address_city: formData.seller.address_city || undefined,
        address_state: formData.seller.address_state || undefined,
        address_zip: formData.seller.address_zip || undefined,
        is_primary: true,
      });
    }

    // Lender
    if (formData.lender.name.trim()) {
      parties.push({
        party_type: 'lender',
        entity_type: 'organization',
        name: formData.lender.name,
        email: formData.lender.email || undefined,
        phone: formData.lender.phone || undefined,
        address_street: formData.lender.address_street || undefined,
        address_city: formData.lender.address_city || undefined,
        address_state: formData.lender.address_state || undefined,
        address_zip: formData.lender.address_zip || undefined,
        is_primary: true,
      });
    }

    // Listing Agent
    if (formData.listing_agent.name.trim()) {
      parties.push({
        party_type: 'listing_agent',
        name: formData.listing_agent.name,
        email: formData.listing_agent.email || undefined,
        phone: formData.listing_agent.phone || undefined,
        is_primary: true,
      });
    }

    // Selling Agent
    if (formData.selling_agent.name.trim()) {
      parties.push({
        party_type: 'selling_agent',
        name: formData.selling_agent.name,
        email: formData.selling_agent.email || undefined,
        phone: formData.selling_agent.phone || undefined,
        is_primary: true,
      });
    }

    return parties;
  };

  const handleNext = async () => {
    let isValid = true;

    switch (currentStep) {
      case 1:
        isValid = validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      case 3:
        isValid = validateStep3();
        break;
      case 4:
        isValid = validateStep4();
        break;
      case 5:
        isValid = validateStep5();
        break;
      case 6:
        isValid = validateStep6();
        if (isValid) {
          if (isEditMode && orderId) {
            // Update the order
            const updateData: UpdateOrderInput = {
              transaction_type: formData.transaction_type,
              property_address: formData.property_address,
              property_unit: formData.property_unit || undefined,
              property_city: formData.property_city,
              property_state: formData.property_state,
              property_zip: formData.property_zip,
              property_county: formData.property_county || undefined,
              settlement_agency: formData.settlement_agency || undefined,
              sale_price: formData.sale_price ? Number(formData.sale_price) : undefined,
              target_closing_date: formData.target_closing_date || undefined,
              order_name: formData.order_name || undefined,
              notes: formData.notes || undefined,
            };
            updateOrder.mutate({ id: orderId, data: updateData });
            return; // Don't increment step, wait for success callback
          } else {
            // Create the order
            const orderData: CreateOrderInput = {
              transaction_type: formData.transaction_type,
              property_address: formData.property_address,
              property_unit: formData.property_unit || undefined,
              property_city: formData.property_city,
              property_state: formData.property_state,
              property_zip: formData.property_zip,
              property_county: formData.property_county || undefined,
              settlement_agency: formData.settlement_agency || undefined,
              sale_price: formData.sale_price ? Number(formData.sale_price) : undefined,
              target_closing_date: formData.target_closing_date || undefined,
              order_number: formData.use_auto_order_number ? undefined : formData.order_number,
              order_name: formData.order_name || undefined,
              notes: formData.notes || undefined,
              parties: buildParties(),
            };

            createOrder.mutate(orderData);
            return; // Don't increment step, wait for success callback
          }
        }
        break;
    }

    if (isValid && currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Navigate to the order detail page
    if (createdOrderId) {
      navigate(`/orders/${createdOrderId}`);
    }
  };

  const handleCreateCD = () => {
    // Navigate to create CD with order context
    if (createdOrderId) {
      navigate(`/dashboard/create-cd?orderId=${createdOrderId}`);
    }
  };

  const handleUploadCD = () => {
    // Navigate to order detail page for now - upload feature coming soon
    if (createdOrderId) {
      navigate(`/orders/${createdOrderId}`);
    }
  };

  const handleStepClick = (stepId: number) => {
    // Only allow clicking on completed steps (steps before current)
    // Don't allow navigation after order is created (step 7)
    if (stepId < currentStep && currentStep < 7) {
      setCurrentStep(stepId);
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-8 overflow-x-auto">
      <div className="flex items-center min-w-max">
        {STEPS.map((step, index) => {
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;
          const isClickable = isCompleted && currentStep < 7;

          return (
            <div
              key={step.id}
              className={`flex items-center ${index < STEPS.length - 1 ? 'flex-1' : ''}`}
            >
              <button
                type="button"
                onClick={() => handleStepClick(step.id)}
                disabled={!isClickable}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0 transition-all ${
                  isCompleted
                    ? 'bg-green-500 text-white'
                    : isCurrent
                      ? 'bg-blue-500 text-white'
                      : 'bg-muted text-muted-foreground'
                } ${isClickable ? 'cursor-pointer hover:ring-2 hover:ring-green-500/50 hover:scale-105' : 'cursor-default'}`}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : step.id}
              </button>
              <button
                type="button"
                onClick={() => handleStepClick(step.id)}
                disabled={!isClickable}
                className={`ml-2 hidden lg:block text-left ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <p
                  className={`text-xs font-medium transition-colors ${
                    isCurrent
                      ? 'text-blue-600 dark:text-blue-400'
                      : isClickable
                        ? 'text-muted-foreground hover:text-foreground'
                        : 'text-muted-foreground'
                  }`}
                >
                  {step.title}
                </p>
              </button>
              {index < STEPS.length - 1 && <div className="flex-1 h-px bg-border mx-2 min-w-4" />}
            </div>
          );
        })}
      </div>
    </div>
  );

  const handleContactSelect = (
    partyType: 'buyer' | 'seller' | 'lender' | 'listing_agent' | 'selling_agent',
    contactData: ContactData,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [partyType]: {
        name: contactData.name,
        email: contactData.email,
        phone: contactData.phone,
        address_street: contactData.address_street,
        address_city: contactData.address_city,
        address_state: contactData.address_state,
        address_zip: contactData.address_zip,
      },
    }));
  };

  const renderPartyForm = (
    partyType: 'buyer' | 'seller' | 'lender' | 'listing_agent' | 'selling_agent',
    title: string,
    showAddress: boolean = true,
  ) => {
    const party = formData[partyType];
    const nameError = errors[`${partyType}_name`];

    return (
      <div className="space-y-4">
        {/* Contact Search */}
        <div>
          <Label className="mb-2 block">Search Existing Contacts</Label>
          <ContactSearch
            value={party}
            onChange={(data) => handleContactSelect(partyType, data)}
            placeholder={`Search for ${title.toLowerCase()}...`}
            partyLabel={title}
            includeAddress={showAddress}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Search for an existing contact or enter details manually below
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor={`${partyType}_name`}>
              Name{' '}
              {partyType === 'buyer' ||
              (partyType === 'seller' && formData.transaction_type === 'purchase') ? (
                <span className="text-red-500">*</span>
              ) : (
                ''
              )}
            </Label>
            <Input
              id={`${partyType}_name`}
              placeholder={`${title} name`}
              value={party.name}
              onChange={(e) => updatePartyData(partyType, 'name', e.target.value)}
              className={nameError ? 'border-red-500' : ''}
            />
            {nameError && <p className="text-sm text-red-500 mt-1">{nameError}</p>}
          </div>

          <div>
            <Label htmlFor={`${partyType}_email`}>Email</Label>
            <Input
              id={`${partyType}_email`}
              type="email"
              placeholder="email@example.com"
              value={party.email}
              onChange={(e) => updatePartyData(partyType, 'email', e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor={`${partyType}_phone`}>Phone</Label>
            <Input
              id={`${partyType}_phone`}
              type="tel"
              placeholder="(555) 555-5555"
              value={party.phone}
              onChange={(e) => updatePartyData(partyType, 'phone', e.target.value)}
            />
          </div>
        </div>

        {showAddress && (
          <>
            <div>
              <Label htmlFor={`${partyType}_address_street`}>Address</Label>
              <Input
                id={`${partyType}_address_street`}
                placeholder="Street address"
                value={party.address_street}
                onChange={(e) => updatePartyData(partyType, 'address_street', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor={`${partyType}_address_city`}>City</Label>
                <Input
                  id={`${partyType}_address_city`}
                  placeholder="City"
                  value={party.address_city}
                  onChange={(e) => updatePartyData(partyType, 'address_city', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor={`${partyType}_address_state`}>State</Label>
                <Select
                  value={party.address_state}
                  onValueChange={(value) => updatePartyData(partyType, 'address_state', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor={`${partyType}_address_zip`}>ZIP</Label>
                <Input
                  id={`${partyType}_address_zip`}
                  placeholder="12345"
                  value={party.address_zip}
                  onChange={(e) => updatePartyData(partyType, 'address_zip', e.target.value)}
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="transaction_type">
            Transaction Type <span className="text-red-500">*</span>
          </Label>
          <Select
            value={formData.transaction_type}
            onValueChange={(value: TransactionType) => updateFormData('transaction_type', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select transaction type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="purchase">Purchase</SelectItem>
              <SelectItem value="refinance">Refinance</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground mt-1">
            {formData.transaction_type === 'purchase'
              ? 'A real estate purchase transaction with buyer and seller'
              : 'A refinance transaction with existing homeowner'}
          </p>
        </div>

        <div>
          <Label htmlFor="settlement_agency">Settlement Agency</Label>
          <Input
            id="settlement_agency"
            placeholder="e.g., Peak Title Company"
            value={formData.settlement_agency}
            onChange={(e) => updateFormData('settlement_agency', e.target.value)}
          />
          <p className="text-sm text-muted-foreground mt-1">
            The title company or settlement agent handling this transaction
          </p>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-3">
            <Label htmlFor="property_address">
              Property Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="property_address"
              placeholder="123 Main Street"
              value={formData.property_address}
              onChange={(e) => updateFormData('property_address', e.target.value)}
              className={errors.property_address ? 'border-red-500' : ''}
            />
            {errors.property_address && (
              <p className="text-sm text-red-500 mt-1">{errors.property_address}</p>
            )}
          </div>

          <div>
            <Label htmlFor="property_unit">Unit/Apt</Label>
            <Input
              id="property_unit"
              placeholder="e.g., 4B"
              value={formData.property_unit}
              onChange={(e) => updateFormData('property_unit', e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="col-span-2 md:col-span-1">
            <Label htmlFor="property_city">
              City <span className="text-red-500">*</span>
            </Label>
            <Input
              id="property_city"
              placeholder="City"
              value={formData.property_city}
              onChange={(e) => updateFormData('property_city', e.target.value)}
              className={errors.property_city ? 'border-red-500' : ''}
            />
            {errors.property_city && (
              <p className="text-sm text-red-500 mt-1">{errors.property_city}</p>
            )}
          </div>

          <div>
            <Label htmlFor="property_state">
              State <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.property_state}
              onValueChange={(value) => updateFormData('property_state', value)}
            >
              <SelectTrigger className={errors.property_state ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((state) => (
                  <SelectItem key={state.value} value={state.value}>
                    {state.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.property_state && (
              <p className="text-sm text-red-500 mt-1">{errors.property_state}</p>
            )}
          </div>

          <div>
            <Label htmlFor="property_zip">
              ZIP Code <span className="text-red-500">*</span>
            </Label>
            <Input
              id="property_zip"
              placeholder="12345"
              value={formData.property_zip}
              onChange={(e) => updateFormData('property_zip', e.target.value)}
              className={errors.property_zip ? 'border-red-500' : ''}
            />
            {errors.property_zip && (
              <p className="text-sm text-red-500 mt-1">{errors.property_zip}</p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="property_county">County</Label>
          <Input
            id="property_county"
            placeholder="County (optional)"
            value={formData.property_county}
            onChange={(e) => updateFormData('property_county', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="sale_price">Sale Price</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                id="sale_price"
                type="number"
                placeholder="0"
                className="pl-7"
                value={formData.sale_price}
                onChange={(e) => updateFormData('sale_price', e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="target_closing_date">Target Closing Date</Label>
            <Popover open={closingDateOpen} onOpenChange={setClosingDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="target_closing_date"
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.target_closing_date
                    ? format(
                        parse(formData.target_closing_date, 'yyyy-MM-dd', new Date()),
                        'MMMM d, yyyy',
                      )
                    : 'Select a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={
                    formData.target_closing_date
                      ? parse(formData.target_closing_date, 'yyyy-MM-dd', new Date())
                      : undefined
                  }
                  onSelect={(date) => {
                    if (date) {
                      updateFormData('target_closing_date', format(date, 'yyyy-MM-dd'));
                    }
                    setClosingDateOpen(false);
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
          <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="font-medium">
            {formData.transaction_type === 'purchase' ? 'Buyer' : 'Borrower'} Information
          </h3>
          <p className="text-sm text-muted-foreground">
            Primary {formData.transaction_type === 'purchase' ? 'buyer' : 'borrower'} for this
            transaction
          </p>
        </div>
      </div>
      {renderPartyForm(
        'buyer',
        formData.transaction_type === 'purchase' ? 'Buyer' : 'Borrower',
        true,
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center">
          <User className="h-5 w-5 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <h3 className="font-medium">Seller Information</h3>
          <p className="text-sm text-muted-foreground">
            {formData.transaction_type === 'purchase'
              ? 'Primary seller for this transaction'
              : 'Optional - not typically required for refinance'}
          </p>
        </div>
      </div>
      {renderPartyForm('seller', 'Seller', true)}
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-8">
      {/* Lender */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
            <Building className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h3 className="font-medium">Lender Information</h3>
            <p className="text-sm text-muted-foreground">Optional - The lending institution</p>
          </div>
        </div>
        {renderPartyForm('lender', 'Lender', true)}
      </div>

      {/* Real Estate Agents */}
      <div className="border-t pt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/50 rounded-full flex items-center justify-center">
            <Users className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h3 className="font-medium">Real Estate Agents</h3>
            <p className="text-sm text-muted-foreground">Optional - Listing and selling agents</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium mb-3">Listing Agent (Seller's Agent)</h4>
            {renderPartyForm('listing_agent', 'Listing Agent', false)}
          </div>
          <div>
            <h4 className="text-sm font-medium mb-3">Selling Agent (Buyer's Agent)</h4>
            {renderPartyForm('selling_agent', 'Selling Agent', false)}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-muted rounded-lg p-4 space-y-4">
        <h3 className="font-medium">Order Summary</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Transaction Type:</span>
            <span className="ml-2 capitalize">{formData.transaction_type}</span>
          </div>
          {formData.settlement_agency && (
            <div>
              <span className="text-muted-foreground">Settlement Agency:</span>
              <span className="ml-2">{formData.settlement_agency}</span>
            </div>
          )}
        </div>

        <div className="border-t pt-3">
          <p className="text-muted-foreground text-sm mb-1">Property</p>
          <p>
            {formData.property_address}
            {formData.property_unit && `, ${formData.property_unit}`}
          </p>
          <p className="text-muted-foreground">
            {formData.property_city}, {formData.property_state} {formData.property_zip}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-3">
          {formData.buyer.name && (
            <div>
              <p className="text-muted-foreground text-sm">
                {formData.transaction_type === 'purchase' ? 'Buyer' : 'Borrower'}
              </p>
              <p>{formData.buyer.name}</p>
              {formData.buyer.email && (
                <p className="text-sm text-muted-foreground">{formData.buyer.email}</p>
              )}
            </div>
          )}
          {formData.seller.name && (
            <div>
              <p className="text-muted-foreground text-sm">Seller</p>
              <p>{formData.seller.name}</p>
              {formData.seller.email && (
                <p className="text-sm text-muted-foreground">{formData.seller.email}</p>
              )}
            </div>
          )}
        </div>

        {(formData.lender.name || formData.listing_agent.name || formData.selling_agent.name) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-3">
            {formData.lender.name && (
              <div>
                <p className="text-muted-foreground text-sm">Lender</p>
                <p className="text-sm">{formData.lender.name}</p>
              </div>
            )}
            {formData.listing_agent.name && (
              <div>
                <p className="text-muted-foreground text-sm">Listing Agent</p>
                <p className="text-sm">{formData.listing_agent.name}</p>
              </div>
            )}
            {formData.selling_agent.name && (
              <div>
                <p className="text-muted-foreground text-sm">Selling Agent</p>
                <p className="text-sm">{formData.selling_agent.name}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Number & Name */}
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label htmlFor="order_number">Order Number</Label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={formData.use_auto_order_number}
                onChange={(e) => {
                  updateFormData('use_auto_order_number', e.target.checked);
                  if (e.target.checked) {
                    updateFormData('order_number', '');
                  }
                }}
                className="h-4 w-4 rounded border-gray-300"
              />
              Auto-generate
            </label>
          </div>
          <Input
            id="order_number"
            placeholder={
              formData.use_auto_order_number ? 'Will be auto-generated' : 'Enter order number'
            }
            value={formData.use_auto_order_number ? '' : formData.order_number}
            onChange={(e) => updateFormData('order_number', e.target.value)}
            disabled={formData.use_auto_order_number}
            className={errors.order_number ? 'border-red-500' : ''}
          />
          {errors.order_number && (
            <p className="text-sm text-red-500 mt-1">{errors.order_number}</p>
          )}
          {formData.use_auto_order_number && (
            <p className="text-sm text-muted-foreground mt-1">
              Format: ORD-YYYY-XXXX (e.g., ORD-2025-0001)
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="order_name">Order Name</Label>
          <Input
            id="order_name"
            placeholder="Optional friendly name for this order"
            value={formData.order_name}
            onChange={(e) => updateFormData('order_name', e.target.value)}
          />
          <p className="text-sm text-muted-foreground mt-1">
            A descriptive name to help identify this order (e.g., "Smith Purchase")
          </p>
        </div>

        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            placeholder="Add any notes about this order..."
            rows={3}
            value={formData.notes}
            onChange={(e) => updateFormData('notes', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const formatCurrency = (value: string | number | undefined) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(Number(value));
  };

  const getStatusBadgeVariant = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      draft: 'secondary',
      reconciling: 'default',
      approved: 'default',
      finalized: 'outline',
    };
    return variants[status] || 'outline';
  };

  const renderStep7 = () => (
    <div className="space-y-6">
      {/* Success Message */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-lg font-medium">
          {isEditMode ? 'Order Updated Successfully' : 'Order Created Successfully'}
        </h3>
        <p className="text-muted-foreground mt-1">
          {activeCDs.length > 0
            ? `This order has ${activeCDs.length} linked Closing Disclosure${activeCDs.length > 1 ? 's' : ''}.`
            : 'Add your first Closing Disclosure to this order.'}
        </p>
      </div>

      {/* Existing CDs List */}
      {isLoadingCDs ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : activeCDs.length > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm text-muted-foreground">
              Linked Closing Disclosures
            </h4>
            <span className="text-xs text-muted-foreground">
              {activeCDs.length} CD{activeCDs.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="border rounded-lg divide-y">
            {activeCDs.map((cd: OrderTransaction) => (
              <div
                key={cd.id}
                className="p-4 flex items-center justify-between hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/dashboard/cd/${cd.id}/edit`)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{cd.source_type || 'Manual Entry'}</span>
                      <Badge variant={getStatusBadgeVariant(cd.status)} className="text-xs">
                        {cd.status}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-3">
                      {cd.closing_date && (
                        <span>Closing: {new Date(cd.closing_date).toLocaleDateString()}</span>
                      )}
                      {cd.sale_price && <span>{formatCurrency(cd.sale_price)}</span>}
                      {!cd.closing_date && !cd.sale_price && cd.property_address && (
                        <span className="truncate max-w-[200px]">{cd.property_address}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {cd.last_version_date || cd.updatedAt
                      ? new Date(cd.last_version_date || cd.updatedAt).toLocaleDateString()
                      : ''}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    v{cd.version || 1}
                  </Badge>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          className="cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
          onClick={handleCreateCD}
        >
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center mx-auto mb-3">
              <Plus className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h4 className="font-medium">
              {activeCDs.length > 0 ? 'Add Another CD' : 'Create CD from Scratch'}
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              Build a Closing Disclosure step by step
            </p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
          onClick={handleUploadCD}
        >
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center mx-auto mb-3">
              <Upload className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h4 className="font-medium">Upload CD Document</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Import from an existing PDF document
            </p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:border-muted-foreground/50 transition-colors"
          onClick={handleSkip}
        >
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              <FileText className="h-6 w-6 text-muted-foreground" />
            </div>
            <h4 className="font-medium">{activeCDs.length > 0 ? 'Done' : 'Skip for Now'}</h4>
            <p className="text-sm text-muted-foreground mt-1">
              {activeCDs.length > 0
                ? 'Go to order details page'
                : 'Add CDs later from the order page'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      case 7:
        return renderStep7();
      default:
        return null;
    }
  };

  const getStepButtonText = () => {
    if (currentStep === 6) {
      if (isEditMode) {
        return updateOrder.isPending ? 'Saving...' : 'Save Changes';
      }
      return createOrder.isPending ? 'Creating...' : 'Create Order';
    }
    return 'Next';
  };

  const isPending = isEditMode ? updateOrder.isPending : createOrder.isPending;

  // Show loading state when fetching order data in edit mode
  if (isEditMode && isLoadingOrder) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {renderStepIndicator()}

      {errors.submit && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive">{errors.submit}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {STEPS[currentStep - 1]?.icon}
            {STEPS[currentStep - 1]?.title ?? 'Step'}
          </CardTitle>
        </CardHeader>
        <CardContent>{renderCurrentStep()}</CardContent>
      </Card>

      {/* Navigation Buttons */}
      {currentStep < 7 && (
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="outline"
            onClick={
              currentStep === 1
                ? () => navigate(isEditMode ? `/orders/${orderId}` : '/orders')
                : handleBack
            }
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </Button>

          <Button onClick={handleNext} disabled={isPending}>
            {isPending ? (
              <>
                <span className="animate-spin mr-2">...</span>
                {isEditMode ? 'Saving...' : 'Creating...'}
              </>
            ) : currentStep === 6 ? (
              <>
                {isEditMode ? 'Save Changes' : 'Create Order'}
                <Check className="h-4 w-4 ml-2" />
              </>
            ) : (
              <>
                {getStepButtonText()}
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
